import * as THREE from 'three';

/**
 * v1.4 新版的小細節貼圖（全部用 canvas 畫，不需要圖檔）：
 * 螢幕畫面、機台控制面板、輸送帶紋路、公告板。貼圖共用（userData.shared），換樓層不會被釋放。
 */
const cache = new Map<string, THREE.CanvasTexture>();
function make(key: string, w: number, h: number, draw: (x: CanvasRenderingContext2D) => void, repeat = false): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const hit = cache.get(key); if (hit) return hit;
  const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
  const x = cv.getContext('2d'); if (!x) return null;
  draw(x);
  const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace; t.userData.shared = true;
  if (repeat) t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  cache.set(key, t); return t;
}

/** 螢幕：深色底＋發光的介面（曲線、長條、文字列）。tint＝主色 */
export function screenTexture(tint = '#5ec8e6'): THREE.CanvasTexture | null {
  return make(`screen${tint}`, 256, 160, x => {
    x.fillStyle = '#0b1418'; x.fillRect(0, 0, 256, 160);
    x.fillStyle = 'rgba(255,255,255,0.06)'; x.fillRect(0, 0, 256, 22);
    x.fillStyle = tint; x.globalAlpha = 0.95; x.fillRect(10, 8, 60, 7);
    x.fillStyle = '#f0a43a'; x.fillRect(200, 7, 12, 9); x.fillStyle = '#4fd58a'; x.fillRect(218, 7, 12, 9); x.fillStyle = '#e0645a'; x.fillRect(236, 7, 12, 9);
    // 曲線
    x.strokeStyle = tint; x.lineWidth = 4; x.globalAlpha = 1; x.beginPath();
    for (let i = 0; i <= 24; i++) { const px = 10 + i * 6.2, py = 78 - Math.sin(i * 0.55) * 18 - (i % 5) * 2; if (i) x.lineTo(px, py); else x.moveTo(px, py); }
    x.stroke();
    // 長條
    for (let i = 0; i < 6; i++) { x.fillStyle = i % 2 ? tint : '#9fe0c0'; x.globalAlpha = 0.85; const bh = 14 + ((i * 37) % 40); x.fillRect(176 + i * 12, 118 - bh, 8, bh); }
    // 文字列
    x.globalAlpha = 0.6; x.fillStyle = '#d8eef4';
    for (let i = 0; i < 3; i++) x.fillRect(10, 112 + i * 14, 120 - i * 26, 6);
    x.globalAlpha = 1;
  });
}

/** 機台控制面板：按鈕、指示燈、警示貼紙（貼在機台正面） */
export function panelTexture(): THREE.CanvasTexture | null {
  return make('panel', 128, 128, x => {
    x.fillStyle = '#d9dfe2'; x.fillRect(0, 0, 128, 128);
    x.strokeStyle = 'rgba(40,50,56,0.35)'; x.lineWidth = 4; x.strokeRect(4, 4, 120, 120);
    const btn = (cx: number, cy: number, c: string) => { x.fillStyle = '#5b666c'; x.beginPath(); x.arc(cx, cy, 13, 0, Math.PI * 2); x.fill(); x.fillStyle = c; x.beginPath(); x.arc(cx, cy, 9, 0, Math.PI * 2); x.fill(); };
    btn(32, 36, '#4fd58a'); btn(64, 36, '#f0a43a'); btn(96, 36, '#e0645a');
    // 緊急停止（黃底紅鈕）
    x.fillStyle = '#f2c84b'; x.fillRect(42, 64, 44, 44); x.fillStyle = '#c9302c'; x.beginPath(); x.arc(64, 86, 15, 0, Math.PI * 2); x.fill();
    x.fillStyle = '#1d2226'; x.font = '700 11px sans-serif'; x.textAlign = 'center'; x.fillText('STOP', 64, 122);
  });
}

/** 輸送帶：深色皮帶＋橫紋（沿長邊重複） */
export function beltTexture(): THREE.CanvasTexture | null {
  return make('belt', 64, 64, x => {
    x.fillStyle = '#2b3136'; x.fillRect(0, 0, 64, 64);
    x.fillStyle = '#3b434a'; for (let i = 0; i < 4; i++) x.fillRect(i * 16, 0, 7, 64);
    x.fillStyle = 'rgba(255,255,255,0.05)'; x.fillRect(0, 0, 64, 4); x.fillRect(0, 60, 64, 4);
  }, true);
}

/** 公告板：軟木底＋釘著幾張彩色紙 */
export function boardTexture(): THREE.CanvasTexture | null {
  return make('board', 256, 160, x => {
    x.fillStyle = '#c99d6a'; x.fillRect(0, 0, 256, 160);
    x.fillStyle = 'rgba(90,60,30,0.18)'; for (let i = 0; i < 220; i++) x.fillRect((i * 53) % 256, (i * 29) % 160, 2, 2);
    x.strokeStyle = '#8a6a44'; x.lineWidth = 10; x.strokeRect(5, 5, 246, 150);
    const papers: [number, number, number, number, string][] = [[22, 20, 62, 78, '#fbf6ec'], [94, 28, 58, 50, '#f6e08a'], [160, 18, 72, 64, '#cfe9f7'], [104, 90, 66, 52, '#f8c9d6'], [26, 106, 60, 40, '#fbf6ec'], [182, 92, 52, 50, '#d8efd2']];
    for (const [px, py, pw, ph, c] of papers) {
      x.fillStyle = 'rgba(0,0,0,0.18)'; x.fillRect(px + 3, py + 3, pw, ph);
      x.fillStyle = c; x.fillRect(px, py, pw, ph);
      x.fillStyle = 'rgba(59,47,42,0.45)'; for (let i = 0; i < 4; i++) x.fillRect(px + 7, py + 12 + i * 9, pw - 14 - (i % 2) * 12, 3);
      x.fillStyle = '#e0645a'; x.beginPath(); x.arc(px + pw / 2, py + 4, 4, 0, Math.PI * 2); x.fill();
    }
  });
}
